import type { DeploymentDatabaseChanges, DeploymentOutcome, DeploymentStage, LiveSiteHealth, ServingRelation } from '../../api';

/**
 * Impact (UX_ARCHITECTURE.md, rules B and C). What an operation did to
 * production is a different fact from how the operation ended, and from
 * how healthy the resource is now. This module derives the first one from
 * the evidence the server sends — which stages ran, whether the database
 * was modified, whether the release served — and never from the outcome
 * alone. Missing evidence is "not verified", never "no impact".
 */
export type ImpactLevel = 'none_observed' | 'detected' | 'potential' | 'not_verified';

/** What happened to traffic: a value, shown as a field, not a sentence. */
export type TrafficFact = 'never_moved' | 'not_yet' | 'switched' | 'switched_back' | 'serving' | 'superseded' | 'unknown';

export interface ImpactAssessment {
  level: ImpactLevel;
  /**
   * One sentence that says why. It is the accessible description and the
   * tooltip of the impact label, not visible text: the visible evidence is
   * the two fields below.
   */
  summary: string;
  /** Did traffic reach this release? */
  traffic: TrafficFact;
  /** Did the run change the database? */
  database: DatabaseTouch;
}

/** The words of each field value. */
export const trafficWords: Record<TrafficFact, string> = {
  never_moved: 'Never moved',
  not_yet: 'Not moved yet',
  switched: 'Moved',
  switched_back: 'Switched back',
  serving: 'Serving',
  superseded: 'Superseded',
  unknown: 'Not reported',
};

export const databaseWords: Record<DatabaseTouch, string> = {
  untouched: 'Unchanged',
  modified: 'Modified',
  unknown: 'Not reported',
};

/** The words of each level, used by the badge, the headline and the screen reader. */
export const impactWords: Record<ImpactLevel, { label: string; short: string }> = {
  none_observed: { label: 'No observed impact', short: 'no observed production impact' },
  detected: { label: 'Impact detected', short: 'production impact detected' },
  potential: { label: 'Potential impact', short: 'potential production impact' },
  not_verified: { label: 'Impact not verified', short: 'impact not verified' },
};

/**
 * What an operation has done to production so far, by stage. The stage keys
 * are the server's fixed catalog (internal/service/deployment_stages.go);
 * a key this table does not know is treated as unknown, not as harmless.
 */
export type ProductionPhase = 'preparing' | 'candidate' | 'database' | 'traffic' | 'verification' | 'handover' | 'unknown';

const phaseOfStageKey: Record<string, ProductionPhase> = {
  preflight: 'preparing',
  prepare: 'preparing',
  start: 'candidate',
  analyse: 'candidate',
  build: 'candidate',
  verify_candidate: 'candidate',
  checkpoint: 'candidate',
  database: 'database',
  switch: 'traffic',
  verify_live: 'verification',
  workers: 'handover',
  reopen: 'handover',
  retire: 'handover',
  finish: 'handover',
};

export function phaseOfStage(key: string): ProductionPhase {
  return phaseOfStageKey[key] ?? 'unknown';
}

/** The phase, as a short label. What it means for production is the impact's to say. */
export const phaseWords: Record<ProductionPhase, string> = {
  preparing: 'Preparing',
  candidate: 'Candidate build',
  database: 'Database',
  traffic: 'Traffic switch',
  verification: 'Verification',
  handover: 'Hand-over',
  unknown: 'Working',
};

const RAN: DeploymentStage['status'][] = ['running', 'partial', 'succeeded', 'warning', 'failed'];

function stageRan(stages: DeploymentStage[], key: string): boolean {
  const stage = stages.find((candidate) => candidate.key === key);
  return Boolean(stage && RAN.includes(stage.status));
}

export type DatabaseTouch = 'modified' | 'untouched' | 'unknown';

/**
 * Whether the run changed the database, only from evidence: Magento's own
 * "applied" verdict first, the stage record otherwise. No verdict and no
 * stage record is "unknown", which is never reported as untouched.
 */
export function databaseTouch(applied: DeploymentDatabaseChanges['applied'] | undefined, stages: DeploymentStage[]): DatabaseTouch {
  if (applied === 'applied' || applied === 'failed' || applied === 'running') return 'modified';
  if (applied === 'not_needed' || applied === 'not_run') return 'untouched';
  if (applied === 'pending') return stages.some((stage) => stage.key === 'database') && stageRan(stages, 'database') ? 'unknown' : 'untouched';
  if (stages.length === 0) return 'unknown';
  // No verdict (frontends, WordPress, older servers): the stage record alone
  // cannot show the database was left alone, only that its stage never ran.
  return stageRan(stages, 'database') ? 'unknown' : 'untouched';
}

export interface ImpactInput {
  outcome: DeploymentOutcome;
  serving: ServingRelation;
  stages: DeploymentStage[];
  /** `database_changes.applied`; absent when the server reported none. */
  databaseApplied?: DeploymentDatabaseChanges['applied'];
  site?: LiveSiteHealth;
  /** A rehearsal records steps and touches no server. */
  rehearsal?: boolean;
}

function siteIsBad(site: LiveSiteHealth | undefined): boolean {
  return site?.state === 'degraded' || site?.state === 'unavailable';
}

/**
 * assessDeploymentImpact: what the attempt did to production. The site's
 * own health decides "impact detected" only for what is happening now; the
 * summary always says whether the attempt itself is implicated, and never
 * blames it without evidence. The two fields it returns (traffic, database)
 * are the evidence, shown as fields.
 */
export function assessDeploymentImpact(input: ImpactInput): ImpactAssessment {
  const { outcome, serving, stages, site } = input;
  const switched = stageRan(stages, 'switch');
  const verifyLive = stageRan(stages, 'verify_live');
  const trafficKnown = stages.length > 0 || serving === 'never_activated';
  // The server's serving relation is definite when it has one: a release that
  // was never activated never took traffic, whatever the stage record shows.
  // Without one, the stages decide.
  const touched = serving === 'never_activated' ? false : switched || verifyLive || serving === 'serving' || serving === 'superseded' || serving === 'rolled_back';
  const database = databaseTouch(input.databaseApplied, stages);

  let traffic: TrafficFact;
  if (touched) {
    traffic = serving === 'rolled_back' ? 'switched_back' : serving === 'serving' ? 'serving' : serving === 'superseded' ? 'superseded' : 'switched';
  } else if (serving === 'never_activated') {
    traffic = 'never_moved';
  } else if (trafficKnown) {
    traffic = outcome === 'deploying' || outcome === 'queued' ? 'not_yet' : 'never_moved';
  } else {
    traffic = 'unknown';
  }
  const done = (level: ImpactLevel, summary: string): ImpactAssessment => ({ level, summary, traffic, database });

  if (input.rehearsal) return { level: 'none_observed', summary: 'A rehearsal records every step as it would run. No server was touched.', traffic: 'never_moved', database: 'untouched' };
  if (outcome === 'queued') return { level: 'none_observed', summary: 'It has not started. The current release keeps serving.', traffic: 'not_yet', database: 'untouched' };

  const failedAttempt = outcome === 'failed' || outcome === 'succeeded_with_failures' || outcome === 'cancelled' || outcome === 'rolled_back';
  const failedReleaseServes = serving === 'serving' && (outcome === 'failed' || outcome === 'succeeded_with_failures');

  if (failedReleaseServes) {
    return done('detected', outcome === 'succeeded_with_failures' ? 'This release is live and some of its checks failed.' : 'The release that failed went live and serves visitors now.');
  }
  if (siteIsBad(site)) {
    const implicated = touched || database === 'modified';
    // A site that is unwell while the attempt changed nothing is the site's
    // own fact, shown beside the attempt, not an impact of it.
    if (!implicated) return done('none_observed', `Nothing in the record shows this attempt changed production. The site is ${site!.state} now; that is reported separately.`);
    return done('detected', serving === 'serving' ? `The site is ${site!.state} while this release serves it.` : `The site is ${site!.state}, and this attempt had changed production.`);
  }
  if (outcome === 'deploying') {
    if (database === 'modified' || switched) return done('potential', database === 'modified' ? 'Database changes are being or have been applied.' : 'Traffic is moving or has moved to the new release.');
    return done('none_observed', 'So far the run has only prepared and checked the new version. Traffic and the database are unchanged.');
  }
  if (outcome === 'succeeded') {
    if (!site) return done('not_verified', 'The release is live. The site’s health was not reported.');
    if (site.state === 'operational' && site.verified) return done('none_observed', 'The release is live and the site’s checks pass.');
    return done('not_verified', 'The release is live. The site’s checks do not yet confirm it.');
  }
  if (outcome === 'succeeded_with_failures') {
    return done('potential', 'This release took traffic and some of its checks failed before a newer release replaced it.');
  }
  if (failedAttempt) {
    if (database === 'modified') return done('potential', 'The run changed the database before it stopped, so what serves may not match that change.');
    if (touched) return done('potential', 'Traffic had moved to this release before the run stopped.');
    if (database === 'unknown' || !trafficKnown) return done('not_verified', 'Whether this attempt changed production is not reported, so no conclusion is drawn.');
    const unverifiedSite = !site || site.state === 'unknown';
    return done('none_observed', unverifiedSite ? 'Nothing in the record shows this attempt changed production. The site’s own health is not verified.' : 'Nothing in the record shows this attempt changed production.');
  }
  return done('not_verified', 'Not enough is reported to say what this did to production.');
}
